/**
 * Map Store
 */

import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import type { LatLngBounds } from 'leaflet';
import { MAP_CONFIG, type TileServerId } from '@/config/map';

type EntityType = 'plot' | 'concession' | 'owner' | null;

interface SelectedEntity {
  type: EntityType;
  id: string | number | null;
}

interface MapState {
  // View state
  center: [number, number];
  zoom: number;
  bounds: LatLngBounds | null;
  basemap: TileServerId;
  
  // Selection
  selectedEntity: SelectedEntity;
  hoveredEntity: SelectedEntity;
  
  // Interaction modes
  isIntersectMode: boolean;
  isDrawingMode: boolean;
  drawingType: 'circle' | 'polygon' | null;
  intersectPoint: [number, number] | null;
  
  // Actions
  setCenter: (center: [number, number]) => void;
  setZoom: (zoom: number) => void;
  setView: (center: [number, number], zoom: number) => void;
  setBounds: (bounds: LatLngBounds) => void;
  setBasemap: (basemap: TileServerId) => void;
  
  // Selection actions
  selectPlot: (id: number) => void;
  selectConcession: (id: number) => void;
  selectOwner: (ruc: string) => void;
  clearSelection: () => void;
  
  // Hover actions
  hoverPlot: (id: number | null) => void;
  hoverConcession: (id: number | null) => void;
  
  // Mode actions
  startIntersectMode: () => void;
  stopIntersectMode: () => void;
  startDrawingMode: (type: 'circle' | 'polygon') => void;
  stopDrawingMode: () => void;
  setIntersectMode: (active: boolean) => void;
  setIntersectPoint: (point: [number, number] | null) => void;
  clearIntersectPoint: () => void;
  
  // Navigation
  flyTo: (lat: number, lng: number, zoom?: number) => void;
  flyToBounds: (bounds: LatLngBounds, padding?: number) => void;
}

// We'll store a reference to the map for programmatic control
let mapRef: L.Map | null = null;

export const setMapRef = (map: L.Map | null) => {
  mapRef = map;
};

export const getMapRef = () => mapRef;

export const useMapStore = create<MapState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        center: MAP_CONFIG.defaultCenter as [number, number],
        zoom: MAP_CONFIG.defaultZoom,
        bounds: null,
        basemap: MAP_CONFIG.defaultTileServer,
        
        selectedEntity: { type: null, id: null },
        hoveredEntity: { type: null, id: null },
        
        isIntersectMode: false,
        isDrawingMode: false,
        drawingType: null,
        intersectPoint: null,
      
      // View actions
      setCenter: (center) => set({ center }),
      setZoom: (zoom) => {
        if (mapRef && mapRef.getZoom() !== zoom) {
          mapRef.setZoom(zoom);
        }
        set({ zoom });
      },
      setView: (center, zoom) => set({ center, zoom }),
      setBounds: (bounds) => set({ bounds }),
      setBasemap: (basemap) => set({ basemap }),
      
      // Selection actions
      selectPlot: (id) => set({
        selectedEntity: { type: 'plot', id },
      }),
      
      selectConcession: (id) => set({
        selectedEntity: { type: 'concession', id },
      }),
      
      selectOwner: (ruc) => set({
        selectedEntity: { type: 'owner', id: ruc },
      }),
      
      clearSelection: () => set({
        selectedEntity: { type: null, id: null },
      }),
      
      // Hover actions
      hoverPlot: (id) => set({
        hoveredEntity: id ? { type: 'plot', id } : { type: null, id: null },
      }),
      
      hoverConcession: (id) => set({
        hoveredEntity: id ? { type: 'concession', id } : { type: null, id: null },
      }),
      
      // Mode actions
      startIntersectMode: () => set({
        isIntersectMode: true,
        isDrawingMode: false,
        drawingType: null,
      }),
      
      stopIntersectMode: () => set({
        isIntersectMode: false,
      }),
      
      startDrawingMode: (type) => set({
        isDrawingMode: true,
        drawingType: type,
        isIntersectMode: false,
      }),
      
      stopDrawingMode: () => set({
        isDrawingMode: false,
        drawingType: null,
      }),
      
      setIntersectMode: (active) => set({
        isIntersectMode: active,
        intersectPoint: active ? null : get().intersectPoint,
      }),
      
      setIntersectPoint: (point) => set({
        intersectPoint: point,
      }),
      
      clearIntersectPoint: () => set({
        intersectPoint: null,
      }),
      
      // Navigation
      flyTo: (lat, lng, zoom) => {
        if (mapRef) {
          mapRef.flyTo([lat, lng], zoom ?? get().zoom, {
            duration: 1,
          });
        }
        set({ center: [lat, lng], zoom: zoom ?? get().zoom });
      },
      
      flyToBounds: (bounds, padding = 50) => {
        if (mapRef) {
          mapRef.flyToBounds(bounds, {
            padding: [padding, padding],
            duration: 1,
          });
        }
        set({ bounds });
      },
    }),
    {
      name: 'reinfo-map',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist serializable state
        center: state.center,
        zoom: state.zoom,
        basemap: state.basemap,
        selectedEntity: state.selectedEntity,
        // Don't persist: bounds (complex object), hoveredEntity (transient), modes (transient)
      }),
    }
  ),
    { name: 'MapStore' }
  )
);

// Selectors
export const selectSelectedEntity = (state: MapState) => state.selectedEntity;
export const selectBasemap = (state: MapState) => state.basemap;
export const selectIsIntersectMode = (state: MapState) => state.isIntersectMode;
